import type { ApiResponse } from '../../api/contract';
import type { ApiClient, RequestOptions } from '../../api/http';
import type { ApprovalsRepository } from './repository';
import type { ApprovalListQuery } from './types';

type ApprovalListResponse = ApiResponse<'/api/v1/approval-requests', 'get'>;
type ApprovalDetailResponse = ApiResponse<'/api/v1/approval-requests/{id}', 'get'>;
type ApprovalDecisionResponse = ApiResponse<'/api/v1/approval-requests/{id}/decision', 'post'>;
type ApprovalExecutionResponse = ApiResponse<'/api/v1/approval-requests/{id}/execute', 'post'>;

export function createRealApprovalsRepository(
  client: ApiClient,
  accessToken: string | null,
): ApprovalsRepository {
  const auth: RequestOptions = accessToken === null ? {} : { accessToken };
  return {
    async list(query, signal) {
      const response = await client.get<ApprovalListResponse>('/api/v1/approval-requests', {
        ...auth,
        query: approvalListQuery(query),
        signal,
      });
      return page(response);
    },
    async get(approvalId, signal) {
      const response = await client.get<ApprovalDetailResponse>(approvalPath(approvalId), {
        ...auth,
        signal,
      });
      return response.data.data;
    },
    async decide(approvalId, input, idempotencyKey) {
      const response = await client.post<ApprovalDecisionResponse>(
        `${approvalPath(approvalId)}/decision`,
        input,
        { ...auth, idempotencyKey: requireUuid(idempotencyKey) },
      );
      return { data: response.data.data, requestId: response.requestId };
    },
    async execute(approvalId, _input, idempotencyKey) {
      // The spec's /execute operation has no requestBody (unlike the decision endpoint); we still send
      // an empty JSON object since ApiClient#post requires a body argument.
      const response = await client.post<ApprovalExecutionResponse>(
        `${approvalPath(approvalId)}/execute`,
        {},
        { ...auth, idempotencyKey: requireUuid(idempotencyKey) },
      );
      return { data: response.data.data, requestId: response.requestId };
    },
  };
}

function approvalListQuery(query: ApprovalListQuery): Readonly<Record<string, unknown>> {
  const filters = query.filters;
  return compact({
    cursor: query.cursor,
    limit: query.pageSize,
    partnerId: filters.partnerId.trim(),
    actionType: filters.actionType,
    status: filters.status,
    entityType: filters.entityType.trim(),
    entityId: filters.entityId.trim(),
  });
}

function page<Item>(response: {
  readonly data: {
    readonly data: readonly Item[];
    readonly pagination: { readonly nextCursor: string | null };
  };
  readonly requestId: string | null;
}) {
  return {
    items: response.data.data,
    nextCursor: response.data.pagination.nextCursor,
    requestId: response.requestId,
  };
}

function compact(values: Readonly<Record<string, unknown>>) {
  return Object.fromEntries(Object.entries(values).filter(([, value]) => value !== ''));
}

function approvalPath(approvalId: string): string {
  return `/api/v1/approval-requests/${encodeURIComponent(approvalId)}`;
}

function requireUuid(value: string): string {
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(value)) {
    throw new Error('A UUID v4 idempotency key is required');
  }
  return value;
}
